import { TODO_STATUS } from '../constants';

/**
 * Format a user name from user object or ID with users lookup
 * @param {Object|Number} userOrId - User object or user ID
 * @param {Array} users - Array of user objects (optional, for ID lookup)
 * @returns {String} - Formatted name or 'Unknown'
 */
export const getUserName = (userOrId, users = []) => {
  let user = userOrId;
  
  // If it's an ID, look it up in the users array
  if (typeof userOrId === 'number') {
    user = users.find(u => u.id === userOrId);
  }
  
  if (!user) return 'Unknown';
  return `${user.firstName} ${user.lastName}`;
};

/**
 * Format a date to locale date string
 * @param {String|Date} date - Date to format
 * @returns {String} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '-';
  // Extract the YYYY-MM-DD part and parse as local midnight to avoid UTC
  // timezone offset shifting the displayed date by one day
  const str = typeof date === 'string' ? date : new Date(date).toISOString();
  const datePart = str.split('T')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString();
  }
  return new Date(date).toLocaleDateString();
};

/**
 * Format a date time to locale string
 * @param {String|Date} dateTime - DateTime to format
 * @returns {String} - Formatted datetime string
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  return new Date(dateTime).toLocaleString();
};

/**
 * Check if a date is overdue
 * @param {String|Date} dueDate - Due date to check
 * @param {String} status - Task status
 * @returns {Boolean} - True if overdue
 */
export const isOverdue = (dueDate, status) => {
  if (!dueDate || status === TODO_STATUS.DONE) return false;
  return new Date(dueDate) < new Date();
};

/**
 * Get CSS class for status badge
 * @param {String} status - Status value
 * @returns {String} - CSS class name
 */
export const getStatusBadgeClass = (status) => {
  switch (status) {
    case TODO_STATUS.DONE:
      return 'badge-done';
    case TODO_STATUS.IN_PROGRESS:
      return 'badge-in-progress';
    case TODO_STATUS.NOT_STARTED:
    default:
      return 'badge-not-started';
  }
};

/**
 * Calculate age of an item in calendar days.
 * A topic created today = 0, yesterday = 1, etc.
 * Time-of-day is stripped so the result is based purely on calendar date.
 * @param {String|Date} createdAt - Creation date
 * @returns {Number} - Age in calendar days
 */
export const getAgeDays = (createdAt) => {
  if (!createdAt) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const created = new Date(createdAt);
  created.setHours(0, 0, 0, 0);
  const diffTime = today - created;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export default {
  getUserName,
  formatDate,
  formatDateTime,
  isOverdue,
  getStatusBadgeClass,
  getAgeDays,
};
